/**
 * El WhatsApp del candidato mientras lo escribe: un borrador `+<indicativo> <número>`
 * que guarda el país elegido aunque el número todavía esté vacío, y de ahí el
 * E.164 que se manda a `PUT /job-apply/api/me/whatsapp-phone`.
 *
 * Lo usan el alta (las dos puertas: el archivo y el cuestionario) y Perfil.
 * En el cuestionario el borrador vive en `ScreeningDraft.phone` (y en
 * sessionStorage), así que tiene que ser un texto y no un objeto. Los
 * indicativos y el armado del E.164 son los de la evaluación de inglés.
 */
import { PHONE_COUNTRIES, composeE164, splitE164 } from './englishAssessment';

export interface PhoneParts {
  dial: string;
  local: string;
}

const DRAFT_RE = /^\+(\d{1,4}) ([\s\S]*)$/;

/** País y número → borrador. Con el número vacío conserva el país. */
export function toPhoneDraft({ dial, local }: PhoneParts): string {
  return `+${dial.replace(/\D+/g, '')} ${local}`;
}

/**
 * Lo que la persona escribe en el número. Si pega el número completo con «+»
 * (`+1 555 932 8437`), el país sale de ahí y no del selector: si no, el
 * indicativo quedaría dos veces.
 */
export function typedPhone(dial: string, typed: string): string {
  if (typed.trim().startsWith('+')) {
    const digits = typed.replace(/\D+/g, '');
    const match = [...PHONE_COUNTRIES].sort((a, b) => b.dial.length - a.dial.length).find((c) => digits.startsWith(c.dial));
    if (match) return toPhoneDraft({ dial: match.dial, local: digits.slice(match.dial.length) });
  }
  return toPhoneDraft({ dial, local: typed });
}

/**
 * Borrador (o un E.164, o lo que alguien escribió a mano en el campo viejo de
 * texto libre) → país y número.
 */
export function fromPhoneDraft(draft: string | null | undefined): PhoneParts {
  const raw = String(draft ?? '');
  const m = DRAFT_RE.exec(raw);
  if (m && PHONE_COUNTRIES.some((c) => c.dial === m[1])) return { dial: m[1], local: m[2] };
  return splitE164(raw);
}

/** No escribió número (aunque haya elegido país). */
export function isPhoneDraftEmpty(draft: string | null | undefined): boolean {
  return !fromPhoneDraft(draft).local.replace(/\D+/g, '');
}

/**
 * El E.164 del borrador, o null si está vacío o no es un número posible. Un
 * número pegado con «+» de un país que no está en la lista se respeta tal cual:
 * nunca se le pega el indicativo del selector (+57 +44… sería otro número).
 */
export function phoneDraftToE164(draft: string | null | undefined): string | null {
  if (isPhoneDraftEmpty(draft)) return null;
  const { dial, local } = fromPhoneDraft(draft);
  if (local.trim().startsWith('+')) {
    const full = `+${local.replace(/\D+/g, '')}`;
    return /^\+[1-9]\d{7,14}$/.test(full) ? full : null;
  }
  return composeE164(`+${dial}`, local);
}

/** Escribió algo y no es un número posible: el campo es opcional, pero no se guarda basura. */
export function isPhoneDraftInvalid(draft: string | null | undefined): boolean {
  return !isPhoneDraftEmpty(draft) && !phoneDraftToE164(draft);
}
